<template>
  <v-autocomplete
    v-bind="$attrs"
    v-model="model"
    :error-messages="$utils.getIfExists(errorMessages, ['messages'])"
    :search-input.sync="input"
    :label="label"
    :hint="hint"
    :persistent-hint="hint ? true : false"
    :suffix="suffix"
    :items="items"
    item-text="name"
    item-value="id"
    :loading="isLoading"
    :readonly="readonly"
    :no-data-text="`No profiles found`"
  >
    <template #item="{ item }">
      <v-list-item-content>
        <v-list-item-title>{{ item.name }}</v-list-item-title>
        <v-list-item-subtitle v-if="item.updated">
          Created: {{ item.updated | dateToString }}
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action v-if="item != null">
        <v-card class="d-flex" color="transparent" flat>
          <v-btn icon @click.stop="downloadProfile(item)">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon @click.stop="removeProfile(item.id)">
            <v-icon>delete</v-icon>
          </v-btn>
        </v-card>
      </v-list-item-action>
    </template>

    <template v-if="input && !hideUpload" #no-data>
      <InputCSV :onComplete="processProfile">
        <template #activator="{ triggerUpload }">
          <v-list-item @click="triggerUpload">
            <v-list-item-title>
              Upload new profile:
              <strong>{{ input }}</strong>
            </v-list-item-title>
          </v-list-item>
        </template>
      </InputCSV>
    </template>
  </v-autocomplete>
</template>

<script>
/*
Available Profiles:
"SOLAR",
"WIND",
"DEMAND",
"DAILY_OPERATING_STATUS",
"ENERGY_PRICE",
"FUEL_PRICE",
"BUY_LIMIT",
"SELL_LIMIT",
"CAPACITY_FADE"
*/

import Papa from "papaparse";
import download from "downloadjs";
import { mapActions } from "vuex";
import { makeFindMixin } from "feathers-vuex";

export default {
  name: "InputProfile",
  components: {
    InputCSV: () => import("@/components/InputCSV.vue"),
  },
  mixins: [
    makeFindMixin({
      service: "profiles",
      watch: "params",
    }),
  ],
  props: {
    label: {
      type: String,
      required: false,
      default: null,
    },
    hint: { type: String, required: false, default: null },
    suffix: {
      type: String,
      required: false,
    },
    value: {
      type: [Array, Number, Object],
      required: false,
      default() {
        return null;
      },
    },
    objectValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    profileType: {
      type: String,
      required: true,
    },
    showChart: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideUpload: {
      type: Boolean,
      required: false,
      default: false,
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    useHeaders: {
      type: Array,
      default: () => ["date", "value"],
    },
    errorMessages: {
      type: Object,
      required: false,
    },
  },
  computed: {
    model: {
      get() {
        if (this.objectValue && this.value && this.value.id) {
          return this.value.id;
        }
        return this.value;
      },
      set(x) {
        if (this.objectValue) {
          this.$emit("input", { _resource: "PROFILE", id: x });
        } else {
          this.$emit("input", x);
        }
        this.$emit("change");
      },
    },
    profilesParams() {
      return {
        query: { type: this.profileType, $select: ["id", "name", "type"] },
      };
    },
    items() {
      const items = this.profiles;
      if (this.nullable) {
        items.unshift({ name: "None", id: null, value: null });
      }
      return items;
    },
    isLoading() {
      const { isFindPending, isCreatePending } = this.$store.state.profiles;
      return isFindPending || isCreatePending;
    },
  },
  data() {
    return {
      uploadDialog: null,
      input: null,
    };
  },
  created() {},
  beforeDestroy() {
    // this.$store.commit('profiles/clearAll');
  },
  methods: {
    ...mapActions({
      createProfile: "profiles/create",
      getProfile: "profiles/get",
      removeProfile: "profiles/remove",
    }),
    downloadProfile(item) {
      this.getProfile(item.id).then((profile) => {
        const { name } = profile;
        let { value } = profile;
        // check if value is a single dimension array of numbers
        if (Array.isArray(value) && Number.isFinite(value[0])) {
          value = value.map((v) => ({ value: v }));
        }
        const csv = Papa.unparse(value);
        download(csv, `${name}.csv`, "text/csv");
      });
    },
    processProfile(contents) {
      const nonempty = contents.filter((item) => Object.values(item).some((v) => v !== null));
      // Inefficient, but simple
      // check that the headers are present, with case-insensitive matching
      nonempty.forEach((item) => {
        const trimmedProps = Object.fromEntries(
          Object.keys(item).map((k) => [k.toLowerCase().trim(), k])
        );
        this.useHeaders.forEach((header) => {
          if (item[header] != null) {
            return;
          }
          const headerLower = header.toLowerCase().trim();
          if (trimmedProps[headerLower] !== undefined) {
            const originalHeader = trimmedProps[headerLower];
            item[header] = item[originalHeader];
            delete item[originalHeader];
          } else {
            throw Error(
              `Missing header "${header}". <br /> CSV Upload requires the following headers: ${this.useHeaders} `
            );
          }
        });
      });
      console.debug("profile", nonempty);

      return this.createProfile({
        type: this.profileType,
        name: this.input,
        value: nonempty,
      });
    },
  },
};
</script>
